/**
 * @file uploadAutomationConstants.js
 * @description Options, labels, strings and small formatters for the upload
 *              automation module. Time-zone helpers are borrowed from the
 *              report module so both pages read the same client setting.
 */

import {
    DEFAULT_TIME_ZONE_KEY, resolveTimeZone, clockInZone, formatClock,
} from "../reportModule/reportConstants.js";

export { DEFAULT_TIME_ZONE_KEY, resolveTimeZone, clockInZone, formatClock };

export const ROWS_PER_PAGE = 10;

export const MAX_NAME = 120;
export const MAX_DESCRIPTION = 280;
export const MAX_FILE_EXAMPLE = 200;
export const MAX_RECIPIENTS = 25;

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// ── Language ───────────────────────────────────────────────────────
export const LANGUAGE = { EN: "EN", SP: "SP" };

export const LANGUAGE_OPTIONS = [
    { value: LANGUAGE.EN, label: "English" },
    { value: LANGUAGE.SP, label: "Spanish" },
];

export const LANGUAGE_LABEL = {
    [LANGUAGE.EN]: "English",
    [LANGUAGE.SP]: "Spanish",
};

// ── Upload order ───────────────────────────────────────────────────
// Sequencing only: which file phase 1 processes first on a given day. It has
// no relationship to language. The range is wide so a step can be inserted
// between two existing ones without renumbering everything after it.
export const MIN_UPLOAD_ORDER = 1;
export const MAX_UPLOAD_ORDER = 200;

// ── Days ───────────────────────────────────────────────────────────
export const DAY_OF_WEEK_OPTIONS = [
    { value: "mon", label: "M",  full: "Monday",    short: "Mon" },
    { value: "tue", label: "Tu", full: "Tuesday",   short: "Tue" },
    { value: "wed", label: "W",  full: "Wednesday", short: "Wed" },
    { value: "thu", label: "Th", full: "Thursday",  short: "Thu" },
    { value: "fri", label: "F",  full: "Friday",    short: "Fri" },
    { value: "sat", label: "Sa", full: "Saturday",  short: "Sat" },
    { value: "sun", label: "Su", full: "Sunday",    short: "Sun" },
];

export const DAY_KEYS = DAY_OF_WEEK_OPTIONS.map(d => d.value);
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri"];

export const DAY_PRESETS = [
    { label: "Weekdays", days: WEEKDAYS },
    { label: "Every day", days: DAY_KEYS },
    { label: "Weekend", days: ["sat", "sun"] },
];

// ── Defaults ───────────────────────────────────────────────────────
export const NEW_CONFIG = {
    configId: null,
    name: "",
    description: "",
    language: LANGUAGE.EN,
    uploadOrder: 1,
    campaignId: "",
    fileNameExample: "",
    notifyEnabled: false,
    notifyRecipients: [],
    days: [...WEEKDAYS],
    startTime: "08:00",
    endTime: "20:00",
    scheduleIds: [],
    isEnabled: true,
};

// ── Copy ───────────────────────────────────────────────────────────
export const AUTOMATION_STRINGS = {
    ADD_CONFIG:        "Add automation",
    EDIT_CONFIG:       "Edit automation",
    NO_CONFIGS:        "No automations yet. Add one to route a daily file drop into a campaign.",
    LOAD_FAILED:       "Could not load automations. Refresh to try again.",
    SAVE_FAILED:       "Could not save the automation. Check the fields and try again.",
    DELETE_FAILED:     "Could not delete the automation. Try again.",
    DELETE_CONFIRM:    "Delete this automation? The file will stop routing into the campaign.",
    TZ_SOURCE:         "Set in Configuration → Time Settings.",
    REQUIRE_NAME:      "Name the automation.",
    REQUIRE_CAMPAIGN:  "Pick a campaign.",
    REQUIRE_DAYS:      "Pick at least one day.",
    REQUIRE_WINDOW:    "End time must be later than start time.",
    REQUIRE_SCHEDULES: "Pick at least one schedule from the campaign.",
    ORDER_TAKEN:       "Another active automation already uses that order on one of these days.",
    ORDER_RANGE:       `Upload order must be between 1 and 200.`,
    NO_CAMPAIGNS:      "No active campaigns in this language.",
    NO_SCHEDULES:      "This campaign has no active schedules.",
    SCHEDULES_HINT:    "Only the schedules you check will fire for this file.",
    REQUIRE_EXAMPLE:   "Paste an example file name.",
    FILE_EXAMPLE_HINT: "Paste one real file name. The date, time and counter parts are detected for you.",
    NEXT_FILE:         "Next file expected",
    FILE_HISTORY:      "File history",
    NO_FILES:          "Nothing picked up yet for this automation.",
    FILES_FAILED:      "Could not load the file history.",
    DEDUPE_NOTE:       "Each file name is claimed once. Re-dropping the same name is ignored.",
    NOTIFY_HINT:       "One email per run, sent once the file is loaded and the schedules are queued.",
    NOTIFY_RECIPIENTS_HINT: "Type an address and press Enter. Paste a list to add several at once.",
    REQUIRE_NOTIFY_TO: "Add at least one recipient, or turn notifications off.",
    INVALID_EMAIL:     "That doesn't look like an email address.",
    DUPLICATE_EMAIL:   "That address is already on the list.",
};

// ── File tracker ───────────────────────────────────────────────────
export const FILE_STATUS_LABEL = {
    claimed:    "Claimed",
    processing: "Processing",
    completed:  "Completed",
    failed:     "Failed",
    skipped:    "Skipped",
};

// ── Formatters ─────────────────────────────────────────────────────

/** "Mon–Fri", "Every day", "Mon, Wed, Fri" — whichever reads shortest. */
export function describeDays(days = []) {
    const picked = DAY_KEYS.filter(k => days.includes(k));
    if (!picked.length) return "No days";
    if (picked.length === 7) return "Every day";
    if (picked.length === 5 && WEEKDAYS.every(d => picked.includes(d))) return "Mon–Fri";
    if (picked.length === 2 && picked.includes("sat") && picked.includes("sun")) return "Weekends";

    // Collapse a contiguous run into a range; otherwise list them.
    const idx = picked.map(k => DAY_KEYS.indexOf(k)).sort((a, b) => a - b);
    const contiguous = idx.every((n, i) => i === 0 || n === idx[i - 1] + 1);
    const short = (n) => DAY_OF_WEEK_OPTIONS[n].short;
    return contiguous && idx.length > 2
        ? `${short(idx[0])}–${short(idx[idx.length - 1])}`
        : idx.map(short).join(", ");
}

/** "8:00 AM – 8:00 PM" */
export function describeWindow(startTime, endTime) {
    if (!startTime || !endTime) return "—";
    return `${formatClock(startTime)} – ${formatClock(endTime)}`;
}

/** One-line recap shown at the bottom of the dialog. */
export function describeConfig(config, tzKey = DEFAULT_TIME_ZONE_KEY, campaignName = "") {
    const tz = resolveTimeZone(tzKey);
    const parts = [
        `File ${config.uploadOrder}`,
        LANGUAGE_LABEL[config.language] || config.language,
        describeDays(config.days),
        `${describeWindow(config.startTime, config.endTime)} ${tz.key}`,
    ];
    if (campaignName) parts.push(`→ ${campaignName}`);
    return parts.join(" · ");
}